/**
 * Trade taxonomy for the construction company directory.
 *
 * A company's trades come from, in this order of precedence:
 *   1. its OpenStreetMap craft tag,
 *   2. its Companies House SIC codes,
 *   3. words in its name,
 * and finally the OSM office=construction_company tag. Each trade links to the
 * matching trade landing page on this site (`industry`). Safe to import anywhere.
 */

export type TradeSlug =
  | "builders"
  | "construction-companies"
  | "civil-engineering"
  | "roofers"
  | "plumbers"
  | "heating-engineers"
  | "electricians"
  | "carpenters"
  | "scaffolders"
  | "plasterers"
  | "painters-decorators"
  | "glaziers"
  | "flooring"
  | "bricklayers"
  | "insulation"
  | "landscapers"
  | "demolition"
  | "metal-fabricators"
  | "handyman";

export type Trade = {
  slug: TradeSlug;
  /** Plural label, e.g. "Builders". */
  label: string;
  /** Singular noun, e.g. "builder". */
  singular: string;
  /** Plural noun for sentences, e.g. "builders". */
  plural: string;
  /** Slug of the matching /industries/<slug> landing page. */
  industry: string;
  /** schema.org type for a company whose main trade this is. */
  schemaType: "GeneralContractor" | "RoofingContractor" | "Electrician" | "Plumber" | "HVACBusiness" | "HousePainter" | "HomeAndConstructionBusiness";
  /** OSM craft values that mean this trade. */
  crafts: string[];
  /** SIC 2007 code prefixes that mean this trade. */
  sic: string[];
  /** Name pattern that means this trade. */
  name: RegExp;
  /** Extra search words, so "roofing" finds roofers. */
  synonyms: string[];
};

export const TRADES: Trade[] = [
  { slug: "builders", label: "Builders", singular: "builder", plural: "builders", industry: "builders", schemaType: "GeneralContractor", crafts: ["builder"], sic: ["41202"], name: /\bbuild(er|ers|ing)\b/i, synonyms: ["builder", "building", "extensions"] },
  { slug: "construction-companies", label: "Construction companies", singular: "construction company", plural: "construction companies", industry: "building-firms", schemaType: "GeneralContractor", crafts: [], sic: ["41100", "41201"], name: /\b(construction|contractors?|developments?)\b/i, synonyms: ["construction", "contractor", "main contractor"] },
  { slug: "civil-engineering", label: "Civil engineering and groundworks", singular: "civil engineering contractor", plural: "civil engineering contractors", industry: "main-contractors", schemaType: "GeneralContractor", crafts: [], sic: ["42"], name: /\b(civil|civils|groundworks?)\b/i, synonyms: ["civil engineering", "groundworks", "civils"] },
  { slug: "roofers", label: "Roofers", singular: "roofer", plural: "roofers", industry: "roofers", schemaType: "RoofingContractor", crafts: ["roofer"], sic: ["43910"], name: /\broof(er|ers|ing)?\b/i, synonyms: ["roofer", "roofing"] },
  { slug: "plumbers", label: "Plumbers", singular: "plumber", plural: "plumbers", industry: "plumbers", schemaType: "Plumber", crafts: ["plumber"], sic: ["43220"], name: /\bplumb(er|ers|ing)\b/i, synonyms: ["plumber", "plumbing"] },
  { slug: "heating-engineers", label: "Heating and gas engineers", singular: "heating engineer", plural: "heating engineers", industry: "hvac", schemaType: "HVACBusiness", crafts: ["hvac", "heating_engineer"], sic: ["43220"], name: /\b(heating|boilers?|gas|air conditioning|hvac|heat pumps?)\b/i, synonyms: ["heating", "gas engineer", "boiler", "hvac"] },
  { slug: "electricians", label: "Electricians", singular: "electrician", plural: "electricians", industry: "electricians", schemaType: "Electrician", crafts: ["electrician"], sic: ["43210"], name: /\belectric(al|ian|ians|s)?\b/i, synonyms: ["electrician", "electrical", "sparky"] },
  { slug: "carpenters", label: "Carpenters and joiners", singular: "carpenter or joiner", plural: "carpenters and joiners", industry: "carpenters", schemaType: "HomeAndConstructionBusiness", crafts: ["carpenter", "joiner"], sic: ["43320"], name: /\b(joiners?|joinery|carpent(er|ers|ry))\b/i, synonyms: ["carpenter", "joiner", "joinery", "carpentry"] },
  { slug: "scaffolders", label: "Scaffolders", singular: "scaffolder", plural: "scaffolders", industry: "tradesmen", schemaType: "HomeAndConstructionBusiness", crafts: ["scaffolder"], sic: ["43991"], name: /\bscaffold(ing|ers?)?\b/i, synonyms: ["scaffolder", "scaffolding"] },
  { slug: "plasterers", label: "Plasterers", singular: "plasterer", plural: "plasterers", industry: "tradesmen", schemaType: "HomeAndConstructionBusiness", crafts: ["plasterer"], sic: ["43310"], name: /\bplaster(er|ers|ing)\b/i, synonyms: ["plasterer", "plastering", "rendering"] },
  { slug: "painters-decorators", label: "Painters and decorators", singular: "painter and decorator", plural: "painters and decorators", industry: "painters-decorators", schemaType: "HousePainter", crafts: ["painter"], sic: ["43341"], name: /\b(painters?|decorat(or|ors|ing))\b/i, synonyms: ["painter", "decorator", "decorating"] },
  { slug: "glaziers", label: "Glaziers and window fitters", singular: "glazier", plural: "glaziers and window fitters", industry: "home-improvement", schemaType: "HomeAndConstructionBusiness", crafts: ["glaziery", "window_construction"], sic: ["43342"], name: /\b(glaz(ier|iers|ing)|windows?)\b/i, synonyms: ["glazier", "glazing", "windows"] },
  { slug: "flooring", label: "Flooring and tiling", singular: "flooring or tiling firm", plural: "flooring and tiling firms", industry: "flooring", schemaType: "HomeAndConstructionBusiness", crafts: ["floorer", "tiler"], sic: ["43330"], name: /\b(floor(ing|s)?|til(er|ers|ing))\b/i, synonyms: ["flooring", "tiler", "tiling"] },
  { slug: "bricklayers", label: "Bricklayers and stonemasons", singular: "bricklayer or stonemason", plural: "bricklayers and stonemasons", industry: "builders", schemaType: "HomeAndConstructionBusiness", crafts: ["bricklayer", "stonemason"], sic: [], name: /\b(brick(work|layers?|laying)?|stonemasons?|masonry)\b/i, synonyms: ["bricklayer", "brickwork", "stonemason", "masonry"] },
  { slug: "insulation", label: "Insulation installers", singular: "insulation installer", plural: "insulation installers", industry: "home-improvement", schemaType: "HomeAndConstructionBusiness", crafts: ["insulation"], sic: [], name: /\binsulat(ion|ors?)\b/i, synonyms: ["insulation"] },
  { slug: "landscapers", label: "Landscapers", singular: "landscaper", plural: "landscapers", industry: "landscapers", schemaType: "HomeAndConstructionBusiness", crafts: ["landscaper"], sic: [], name: /\blandscap(e|er|ers|ing)\b/i, synonyms: ["landscaper", "landscaping"] },
  { slug: "demolition", label: "Demolition and site preparation", singular: "demolition contractor", plural: "demolition contractors", industry: "main-contractors", schemaType: "GeneralContractor", crafts: [], sic: ["4311", "4312", "4313"], name: /\bdemolition\b/i, synonyms: ["demolition", "site clearance"] },
  { slug: "metal-fabricators", label: "Steel and metal fabricators", singular: "metal fabricator", plural: "metal fabricators", industry: "tradesmen", schemaType: "HomeAndConstructionBusiness", crafts: ["metal_construction"], sic: [], name: /\b(steel|fabricat(ion|ors?)|welding)\b/i, synonyms: ["steel", "fabrication", "welding"] },
  { slug: "handyman", label: "Handyman services", singular: "handyman service", plural: "handyman services", industry: "handyman", schemaType: "HomeAndConstructionBusiness", crafts: ["handyman"], sic: [], name: /\bhandym[ae]n\b/i, synonyms: ["handyman", "odd jobs"] },
];

const TRADE_BY_SLUG = new Map<string, Trade>(TRADES.map((t) => [t.slug, t]));

export function getTrade(slug: string | null | undefined): Trade | null {
  if (!slug) return null;
  return TRADE_BY_SLUG.get(slug) ?? null;
}

/** Every OSM craft value the import asks Overpass for. */
export const IMPORT_CRAFTS: string[] = Array.from(new Set(TRADES.flatMap((t) => t.crafts))).sort();

export function tradesFromCraft(craft: string | null | undefined): TradeSlug[] {
  if (!craft) return [];
  const values = craft.split(";").map((v) => v.trim().toLowerCase());
  return TRADES.filter((t) => t.crafts.some((c) => values.includes(c))).map((t) => t.slug);
}

export function tradesFromSic(codes: string[] | null | undefined): TradeSlug[] {
  if (!codes || codes.length === 0) return [];
  return TRADES.filter((t) => t.sic.some((prefix) => codes.some((code) => code.startsWith(prefix)))).map((t) => t.slug);
}

export function tradesFromName(name: string | null | undefined): TradeSlug[] {
  if (!name) return [];
  return TRADES.filter((t) => t.name.test(name)).map((t) => t.slug);
}

/**
 * Trades for one company, by precedence: craft tag, then SIC codes, then name,
 * then the office tag. Returns at least one trade for anything the import keeps.
 */
export function deriveTrades(input: { craft?: string | null; sic?: string[] | null; name?: string | null; office?: string | null }): TradeSlug[] {
  const fromCraft = tradesFromCraft(input.craft);
  if (fromCraft.length) return fromCraft;
  const fromSic = tradesFromSic(input.sic);
  if (fromSic.length) return fromSic;
  const fromName = tradesFromName(input.name);
  if (fromName.length) return fromName;
  return ["construction-companies"];
}

/** Lower-case search words for a set of trades, so "roofing" finds roofers. */
export function tradeSearchTerms(slugs: string[]): string[] {
  return slugs.flatMap((slug) => {
    const t = getTrade(slug);
    return t ? [t.plural, t.singular, ...t.synonyms] : [];
  });
}

export function primaryTrade(slugs: string[] | null | undefined): Trade | null {
  if (!slugs) return null;
  for (const slug of slugs) {
    const t = getTrade(slug);
    if (t) return t;
  }
  return null;
}
